<script setup lang="ts">
import { ref } from 'vue'
import type { Bookmark } from '../types/bookmark'
import { formatDateTimeInTimezone } from '../utils/datetimeUtil'

interface Props {
  bookmarks: Bookmark[]
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  loading: false,
})

const deleting = ref<number | null>(null)

const formatTime = (utcDatetime: string): string => {
  if (!utcDatetime) {
    console.warn('BookmarkList: Empty utcDatetime', utcDatetime)
    return '—'
  }
  try {
    return formatDateTimeInTimezone(utcDatetime, 'Asia/Taipei', 'YYYY-MM-DD HH:MM')
  } catch (err) {
    console.error('BookmarkList: formatTime error', err, utcDatetime)
    return utcDatetime
  }
}

const handleDelete = async (id: number) => {
  try {
    deleting.value = id
    emit('delete', id)
  } finally {
    deleting.value = null
  }
}

const handleBookmarkClick = (bookmark: Bookmark) => {
  emit('select', bookmark)
}

const emit = defineEmits<{
  (e: 'delete', id: number): void
  (e: 'select', bookmark: Bookmark): void
}>()
</script>

<template>
  <div class="bookmark-list">
    <div v-if="loading" class="state-message">加載中...</div>

    <div v-else-if="bookmarks.length === 0" class="state-message">暫無書籤</div>

    <div v-else class="bookmarks-container">
      <div
        v-for="bookmark in bookmarks"
        :key="bookmark.id"
        class="bookmark-item"
        @click="handleBookmarkClick(bookmark)"
      >
        <div class="bookmark-content">
          <div class="bookmark-label">{{ bookmark.label }}</div>
          <div class="bookmark-time">{{ formatTime(bookmark.createdAt) }}</div>
        </div>

        <button
          class="delete-btn"
          :disabled="deleting === bookmark.id"
          @click.stop="handleDelete(bookmark.id)"
          title="刪除書籤"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bookmark-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.state-message {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  color: #9ca3af;
  font-size: 13px;
}

.bookmarks-container {
  display: flex;
  flex-direction: column;
}

.bookmark-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.bookmark-item:hover {
  background-color: #fafafa;
}

.bookmark-content {
  flex: 1;
  min-width: 0;
}

.bookmark-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  word-break: break-word;
}

.bookmark-time {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.delete-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border: none;
  background: none;
  color: #d1d5db;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover:not(:disabled) {
  color: #ef4444;
}

.delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
